import React, { useState, useEffect, useCallback } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { StatusActionButton, StatsGrid, StatItem, HorizontalCategoryTabs } from '../../../shared/ui';
import { questionsV2Api, QuestionV2 } from '../../../api/questionsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useDashboardCounters } from '../../../shared/contexts/DashboardCountersContext';
import CreateNoteV2Modal from './CreateNoteV2Modal';
import QuestionV2MessengerModal from '../../questionsV2/ui/QuestionV2MessengerModal';
import './NotesCard.css';

interface NotesCardProps {
  siteId?: number;
}

const NotesCard: React.FC<NotesCardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true); // Ленивая загрузка: по умолчанию свернута
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false); // Отслеживаем открытие fullscreen
  const [notes, setNotes] = useState<QuestionV2[]>([]);
  const [statistics, setStatistics] = useState<{ totalNotes: number; openNotes: number; closedNotes: number; userNotes: number } | null>(null);
  const [loading, setLoading] = useState(false); // Начинаем с false, т.к. не загружаем сразу
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMessengerModal, setShowMessengerModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<QuestionV2 | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'closed' | 'my'>('all');
  const { user } = useAuth();
  const countersContext = useDashboardCounters();
  
  // Получаем счетчик из Context (если доступен) или null
  const counter = countersContext?.counters?.activeNotes.hasAccess 
    ? countersContext.counters.activeNotes.count 
    : null;
  const countersLoading = countersContext?.loading ?? false;

  // Загрузка данных
  const loadData = useCallback(async () => {
    if (!siteId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      const notesData = await questionsV2Api.getSiteContentByTypeV2(siteId, 2); // 2 = Note
      
      setNotes(notesData);
      
      // Вычисляем статистику
      const stats = {
        totalNotes: notesData.length,
        openNotes: notesData.filter((n: QuestionV2) => !n.closed).length,
        closedNotes: notesData.filter((n: QuestionV2) => n.closed).length,
        userNotes: notesData.filter((n: QuestionV2) => n.userId === user?.id).length
      };
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading notes:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId, user?.id]);

  // Счетчики загружаются через Context в ObjectPage

  // Ленивая загрузка: загружаем данные когда карточка раскрыта (inline) или fullscreen модальное окно открыто
  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      loadData();
    }
  }, [isCollapsed, isFullscreenOpen, loadData]);

  // Обработчики событий
  const handleCreateNote = () => {
    setShowCreateModal(true);
  };

  const handleNoteCreated = () => {
    setShowCreateModal(false);
    loadData();
  };

  const handleNoteClick = (note: QuestionV2) => {
    setSelectedNote(note);
    setShowMessengerModal(true);
  };

  const handleNoteUpdated = () => {
    loadData();
  };

  // Стабильный колбэк для закрытия чата — иначе UniversalHeader внутри модалки
  // перезапускает эффект при каждом обновлении контекста и возникает цикл showBack
  const handleCloseMessenger = useCallback(() => {
    setShowMessengerModal(false);
    setSelectedNote(null);
  }, []);

  // Фильтрация заметок
  const getFilteredNotes = () => {
    switch (filter) {
      case 'open':
        return notes.filter(note => !note.closed);
      case 'closed':
        return notes.filter(note => note.closed);
      case 'my':
        return notes.filter(note => note.userId === user?.id);
      default:
        return notes;
    }
  };

  // Создание элементов списка
  const createNoteItems = (): QuestionV2[] => {
    return getFilteredNotes();
  };

  const noteItems = createNoteItems();

  // Подготовка данных для StatsGrid
  const statsData: StatItem[] = [
    {
      id: 'total',
      value: statistics?.totalNotes || 0,
      label: 'Всего',
      icon: 'ri-file-text-line',
      color: 'muted'
    },
    {
      id: 'open',
      value: statistics?.openNotes || 0,
      label: 'Открытых',
      icon: 'ri-lock-unlock-line',
      color: 'success'
    },
    {
      id: 'closed',
      value: statistics?.closedNotes || 0,
      label: 'Закрытых',
      icon: 'ri-lock-line',
      color: 'error'
    },
    {
      id: 'my',
      value: statistics?.userNotes || 0,
      label: 'Мои',
      icon: 'ri-user-line',
      color: 'primary'
    }
  ];

  return (
    <DashboardCard
      id="notes-card"
      title="Заметки"
      icon="ri-sticky-note-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      onFullscreenOpenChange={setIsFullscreenOpen}
      expandMode="fullscreen"
      compactTileVariant="notes"
      counters={counter !== null ? [counter] : undefined}
      countersLoading={countersLoading}
    >
      {!siteId ? (
        <div className="notes-placeholder">
          <i className="ri-file-text-line"></i>
          <h3>Выберите проект</h3>
          <small>Для работы с заметками необходимо выбрать конкретный проект</small>
        </div>
      ) : (
        <>
          {/* Кнопка создания заметки */}
          <div style={{ padding: '0 0 20px 0' }}>
            <StatusActionButton
              variant="primary"
              fullWidth
              onClick={handleCreateNote}
            >
              Создать заметку
            </StatusActionButton>
          </div>

          {/* Статистика */}
          <StatsGrid items={statsData} />

          {/* Фильтры */}
          <HorizontalCategoryTabs
            categories={[
              `Открытые (${statistics?.openNotes || 0})`,
              `Закрытые (${statistics?.closedNotes || 0})`,
              `Мои (${statistics?.userNotes || 0})`
            ]}
            value={
              filter === 'all' 
                ? null 
                : filter === 'open' 
                  ? `Открытые (${statistics?.openNotes || 0})`
                  : filter === 'closed'
                    ? `Закрытые (${statistics?.closedNotes || 0})`
                    : `Мои (${statistics?.userNotes || 0})`
            }
            onChange={(category) => {
              if (category === null) {
                setFilter('all');
              } else if (category.startsWith('Открытые')) {
                setFilter('open');
              } else if (category.startsWith('Закрытые')) {
                setFilter('closed');
              } else if (category.startsWith('Мои')) {
                setFilter('my');
              }
            }}
            showAllOption={true}
            allOptionLabel={`Все (${statistics?.totalNotes || 0})`}
            size="medium"
            className="notes-filters"
            style={{ marginBottom: '20px' }}
          />

          {/* Список заметок */}
          <div className="notes-list">
            {loading ? (
              <div className="notes-loading">
                <i className="ri-loader-4-line ri-spin"></i>
                <span>Загрузка заметок...</span>
              </div>
            ) : noteItems.length === 0 ? (
              <div className="notes-empty">
                <i className="ri-file-text-line"></i>
                <span>Заметок пока нет</span>
                <small>Создайте первую заметку, чтобы начать работу</small>
              </div>
            ) : (
              noteItems.map((note) => (
                <div
                  key={note.id}
                  className={`note-item ${note.closed ? 'closed' : ''}`}
                  onClick={() => handleNoteClick(note)}
                >
                  <div className="note-avatar">
                    <i className={note.closed ? 'ri-lock-line' : 'ri-file-text-line'}></i>
                  </div>
                  
                  <div className="note-content">
                    <h3 className="note-title">{note.text}</h3>
                    <div className="note-subtitle">
                      {note.user?.name || 'Неизвестный'} • {note.typeOfWorks?.name || 'Без типа работы'}
                    </div>
                    <div className="note-details">
                      <span className="note-detail">
                        <i className="ri-time-line"></i>
                        {note.createdAt ? new Date(note.createdAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Дата неизвестна'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="note-arrow">
                    <i className="ri-arrow-right-s-line"></i>
                  </div>
                </div>
              ))
            )}
          </div>

      {/* Модальное окно создания заметки */}
      <CreateNoteV2Modal
        isOpen={showCreateModal}
        siteId={siteId}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleNoteCreated}
      />

          {/* Модальное окно с мессенджером */}
          <QuestionV2MessengerModal
            isOpen={showMessengerModal}
            question={selectedNote}
            siteId={siteId!}
            onClose={handleCloseMessenger}
            onQuestionUpdated={handleNoteUpdated}
          />
        </>
      )}
    </DashboardCard>
  );
};

export default NotesCard;